import { Input, type InputProps } from '@mui/joy';

import { type Mode, type NumericFormat } from './NumberInput.types';

import { MODE_PROPS, MODES } from './NumberInput.constants';

import toDecimal from '@/shared/utils/decimal';

import NumericFormatAdapter from './NumericFormatAdapter';

type Values = {
  asNumber: number | null;
  value: string | null;
};

const parsers = {
  in: (value: string | number | null | undefined) => (value === null ? '' : value),
  out: (value: string) =>
    value === ''
      ? {
          asNumber: null,
          value: null,
        }
      : {
          asNumber: toDecimal(value).toNumber(),
          value,
        },
};

const getModeProps = (mode: Mode = MODES.DEFAULT) => MODE_PROPS[mode];

type Props = {
  className?: string;
  defaultValue?: string | number | null;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (values: Values) => void;
  mode?: Mode;
  name?: string;
  numericFormat?: NumericFormat;
  readOnly?: boolean;
  renderInput?: (props: InputProps) => React.ReactNode;
  size?: InputProps['size'];
  value?: string | number | null;
};

const NumberInput: React.FC<Props> = ({
  className,
  defaultValue,
  disabled,
  mode,
  name,
  numericFormat,
  onChange,
  onValueChange,
  readOnly,
  renderInput,
  size,
  value,
}) => {
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const values = parsers.out(e.target.value);

    onChange?.(e);
    onValueChange?.(values);
  };

  const inputProps = {
    className,
    defaultValue: parsers.in(defaultValue),
    disabled,
    name,
    onChange: handleChange,
    readOnly,
    size,
    slotProps: {
      input: {
        ...getModeProps(mode),
        ...numericFormat,
        component: NumericFormatAdapter,
      },
    },
    value: parsers.in(value),
  } satisfies InputProps;

  return renderInput?.(inputProps) ?? <Input {...inputProps} />;
};

export default NumberInput;
